import { decisionFraction } from "../types/types";
import { Trophy, Medal, Award, Eye, EyeOff, EyeOffIcon, Settings, ChevronLeft, ChevronRight, ChevronDown, Info } from "lucide-react";
import Link from "next/link";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { Avatar, AvatarImage, AvatarFallback } from "@/app/_module/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type { LeaderboardAgent, LeaderboardStats } from "../types/types";

/** "Demo Agent" → "Demo A.": first name and last initial, how Direct130 shows its board. */
const shortName = (name: string) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.` : parts[0] || "";
};

interface SimpleLeaderboardTableProps {
  agents: LeaderboardAgent[];
  stats?: LeaderboardStats;
  period: string;
  metric: string;
  searchQuery?: string;
  onVisibilityChange?: (visible: boolean) => Promise<void>;
  loading?: boolean;
  rawData?: unknown;
  isGlobalLeaderboard?: boolean;
}

export const SimpleLeaderboardTable = function({
  agents = [],
  metric,
  onVisibilityChange,
  loading = false,
  isGlobalLeaderboard = false,
}: SimpleLeaderboardTableProps) {
  const [showOptOutMessage, setShowOptOutMessage] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  // Shell has no hover and Radix's popper drifts in WKWebView, so the column
  // ⓘ becomes tap-to-toggle there: a fixed panel placed from the icon's rect
  // (below it — side="top" put it under the sticky header/off-screen).
  const isShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  const [helpTip, setHelpTip] = useState<{
    key: string;
    top: number;
    left: number;
    text: string;
  } | null>(null);
  const { user } = useUserContext(); // Get current user from context
  const onDirect130 = useDirect130Host(); // Direct130's board: no agency column, no % to goal, first name + last initial (owner 2026-11-01)
  const queryClient = useQueryClient(); // Get query client to invalidate cache

  // Fetch user's visibility settings directly from API (separate from leaderboard data)
  const { data: visibilityData, refetch: refetchVisibility } = useQuery({
    queryKey: ['leaderboard-visibility'],
    queryFn: async () => {
      return await handleApiCall<{ data: { showOnLeaderboard: boolean } }>(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leaderboard.getUserVisibilitySettings),
        });
        return response;
      });
    },
    staleTime: 30000, // 30 seconds
    refetchOnMount: true,
  });

  // Get visibility from dedicated API endpoint
  const userOptedIn = visibilityData?.data?.showOnLeaderboard ?? true;

  // Find current user in the leaderboard using proper user identification
  const currentAgent = agents.find(agent => {
    // First try to match by userId (most reliable)
    if (agent.userId && user.user?.id) {
      return agent.userId === user.user.id;
    }
    // Fallback to name matching if userId not available
    if (agent.name && user.user?.name) {
      return agent.name.toLowerCase().trim() === user.user.name.toLowerCase().trim();
    }
    // Fallback to email matching if available in agent name
    if (agent.name && user.user?.email) {
      return agent.name.toLowerCase().includes(user.user.email.toLowerCase()) ||
             user.user.email.toLowerCase().includes(agent.name.toLowerCase());
    }
    return false;
  });

  // Log for debugging
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Award className="h-5 w-5 text-amber-600" />;
    return <span className="text-lg font-bold text-gray-500">#{rank}</span>;
  };

  const getMetricValue = (agent: LeaderboardAgent, selectedMetric: string): string => {
    switch (selectedMetric) {
      case 'total-policies':
        return agent.totalPolicies?.toString() || agent.totalSales?.toString() || '0';
      case 'total-premium':
        return `$${agent.totalPremium.toLocaleString()}`;
      case 'total-premium-sold':
        return `$${(agent.issuedPremium || 0).toLocaleString()}`;
      case 'appointments':
        return agent.appointments.toString();
      case 'calls-made':
        return agent.callsMade.toString();
      case 'conversion-rate':
        return `${agent.conversionRate?.toFixed(1) || 0}%`;
      case 'goal-progress':
        return agent.goalProgress == null ? '—' : `${agent.goalProgress.toFixed(0)}%`;
      default:
        return `$${agent.totalPremium.toLocaleString()}`;
    }
  };

  const getMetricDisplayName = (selectedMetric: string): string => {
    const names: Record<string, string> = {
      // "Sold" was misleading — this figure includes underwriting. Matches the
      // agency dashboard's "Total Production" box.
      'total-premium': 'Total Production',
      'total-premium-sold': 'Total Premium Sold',
      'total-policies': 'Total Policies',
      'appointments': 'Appointments',
      'calls-made': 'Calls Made',
      'conversion-rate': 'Closing Ratio',
      'goal-progress': '% to Goal',
    };
    return names[selectedMetric] || 'Total Production';
  };

  // What each column actually counts. Premium/policy figures include business
  // still in underwriting, which is NOT obvious from a column called "Total
  // Premium Sold" — an owner comparing this page to the agency dashboard needs
  // to know both are on the same basis, rather than assuming one is wrong.
  const getColumnHelp = (label: string): string | null => {
    switch (label) {
      case 'Total Premium':
      case 'Total Production':
        return 'This is your agency total reported production. Everything marked sold and approved as well as in underwriting.';
      case 'Total Premium Sold':
        return 'Sold business only — everything marked with sold and approved strictly shows up here. Total Production alongside it also includes underwriting.';
      case 'Total Policies':
        return 'Policies that are Sold & Approved or still in underwriting. Same basis as the agency dashboard.';
      case 'Closing Ratio':
        return isGlobalLeaderboard
          ? 'Policies sold / total leads'
          : 'Production closing ratio — (Sold & Approved + Underwriting) ÷ (issued + underwriting + lost). Bad numbers and DNCs excluded as disqualified.';
      default:
        return null;
    }
  };

  const getAgencyDisplay = (agent: LeaderboardAgent) => {
    // If agent has a sub-agency, show only the sub-agency name
    // Otherwise, show the parent agency name
    const displayName = agent.subAgency?.name || agent.agency?.name || 'No Agency';

    return (
      <Badge className="text-xs" variant="outline">
        {displayName}
      </Badge>
    );
  };

  // Define column type interface
  interface ColumnDefinition {
    key: string;
    label: string;
    width?: string;
    isPrimary?: boolean;
  }

  // Define which columns to show based on the selected metric
  const getColumnsToShow = (selectedMetric: string): ColumnDefinition[] => {
    const baseColumns: ColumnDefinition[] = [
      { key: 'rank', label: 'Rank', width: 'w-16' },
      { key: 'agent', label: 'Agent Name' },
      // Only show agency column for agency leaderboards, not global
      ...(isGlobalLeaderboard || onDirect130 ? [] : [{ key: 'agency', label: 'Agency' }]),
    ];

    // FIXED column order, always. Previously the selected metric was pulled out
    // and reinserted as a "primary" column, so choosing Appointments physically
    // moved it into Conversion Rate's position and every neighbouring column
    // shifted — the table appeared to rearrange itself on every switch.
    //
    // Now the layout never changes; selection only changes which column is
    // highlighted, so the eye can stay in one place.
    const METRIC_TO_KEY: Record<string, string> = {
      'total-premium-sold': 'issuedPremium',
      'total-premium': 'totalPremium',
      'conversion-rate': 'conversionRate',
      'goal-progress': 'goalProgress',
      'appointments': 'appointments',
      'calls-made': 'callsMade',
      'total-policies': 'totalPolicies',
    };
    const activeKey = METRIC_TO_KEY[selectedMetric] || 'issuedPremium';

    const dataColumns: ColumnDefinition[] = [
      { key: 'issuedPremium', label: 'Total Premium Sold' },
      { key: 'goalProgress', label: '% to Goal' },
      { key: 'totalPremium', label: 'Total Production' },
      { key: 'conversionRate', label: 'Closing Ratio' },
      { key: 'appointments', label: 'Appointments' },
      { key: 'callsMade', label: 'Calls Made' },
      { key: 'totalPolicies', label: 'Total Policies' },
    ]
      .filter((c) => !(onDirect130 && c.key === 'goalProgress'))
      .map((c) => ({ ...c, isPrimary: c.key === activeKey }));

    return [...baseColumns, ...dataColumns];
  };

  const columns = getColumnsToShow(metric);

  // Pagination logic
  const totalPages = Math.ceil(agents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgents = agents.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when agents change (e.g., search filter applied) or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [agents.length, itemsPerPage]);

  const getCellValue = (agent: LeaderboardAgent, columnKey: string): string => {
    switch (columnKey) {
      case 'primary':
        return getMetricValue(agent, metric);
      case 'totalPremium':
        return `$${(agent.totalPremium || 0).toLocaleString()}`;
      case 'issuedPremium':
        return `$${(agent.issuedPremium || 0).toLocaleString()}`;
      case 'conversionRate': {
        const f = decisionFraction(agent);
        return `${(agent.conversionRate || 0).toFixed(1)}%${f ? ` (${f})` : ''}`;
      }
      case 'goalProgress':
        return agent.goalProgress == null ? '—' : `${agent.goalProgress.toFixed(0)}%${agent.goalLabel ? ` (${agent.goalLabel})` : ''}`;
      case 'appointments':
        return (agent.appointments || 0).toString();
      case 'callsMade':
        return (agent.callsMade || 0).toString();
      case 'totalPolicies':
        return (agent.totalPolicies || agent.totalSales || 0).toString();
      default:
        return '';
    }
  };

  const handleOptInToggle = async (checked: boolean) => {
    if (onVisibilityChange) {
      try {
        setVisibilityLoading(true);
        await onVisibilityChange(checked);

        // CRITICAL FIX: Invalidate the query cache to force refetch and bypass staleTime
        // This ensures the switch reflects the new state immediately
        await queryClient.invalidateQueries({ queryKey: ['leaderboard-visibility'] });
        await refetchVisibility();
      } catch (error) {

      } finally {
        setVisibilityLoading(false);
      }
    }

    if (!checked) {
      setShowOptOutMessage(true);
    }
  };

  // Show loading state
  if (loading && agents.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
              <div className="h-8 bg-gray-200 rounded" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-tour="leaderboard-table">
      {/* Leaderboard Table */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{getMetricDisplayName(metric)} Leaderboard</span>
            <Badge className="text-sm" variant="outline">
              {agents.length} participating agents
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No agents found with the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-50 z-10">
                    <TableRow className="bg-gray-50">
                      {columns.map((column) => (
                        <TableHead
                          key={column.key}
                          className={`font-semibold ${
                            column.width || ''
                          } ${
                            column.key === 'agent' || column.key === 'agency' ? '' : 'text-center'
                          } ${
                            column.isPrimary
                              // Boxed column = the metric the board is sorted by.
                              ? 'bg-primary/5 text-primary font-bold border-x border-t border-primary/30 rounded-t-md'
                              : column.key === 'issuedPremium'
                                ? 'text-emerald-700 font-semibold'
                                : ''
                          }`}
                        >
                          {getColumnHelp(column.label) ? (
                            isShell ? (
                              <span className="inline-flex items-center justify-center gap-1">
                                {column.label}
                                <Info
                                  className="h-3.5 w-3.5 shrink-0 text-gray-400"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (helpTip?.key === column.key) {
                                      setHelpTip(null);
                                      return;
                                    }
                                    const r = e.currentTarget.getBoundingClientRect();
                                    const width = 240;
                                    const left = Math.min(
                                      Math.max(8, r.left + r.width / 2 - width / 2),
                                      window.innerWidth - width - 8,
                                    );
                                    setHelpTip({
                                      key: column.key,
                                      top: r.bottom + 8,
                                      left,
                                      text: getColumnHelp(column.label)!,
                                    });
                                  }}
                                />
                              </span>
                            ) : (
                            <TooltipProvider>
                              <span className="inline-flex items-center justify-center gap-1">
                                {column.label}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 cursor-help text-gray-400" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[260px]">
                                    <p className="text-xs">{getColumnHelp(column.label)}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </span>
                            </TooltipProvider>
                            )
                          ) : (
                            column.label
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAgents.map((agent, index) => {
                      const actualRank = startIndex + index + 1;
                      return (
                        <TableRow
                          key={agent.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {columns.map((column) => {
                            if (column.key === 'rank') {
                              return (
                                <TableCell key={column.key} className="font-medium">
                                  <div className="flex items-center justify-center">
                                    {getRankIcon(actualRank)}
                                  </div>
                                </TableCell>
                              );
                            }

                            if (column.key === 'agent') {
                              const isCurrentUser = (agent.userId && user.user?.id && agent.userId === user.user.id) ||
                                                   (agent.name && user.user?.name && agent.name.toLowerCase().trim() === user.user.name.toLowerCase().trim());

                              return (
                                <TableCell key={column.key}>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="w-10 h-10 shadow-lg">
                                      <AvatarImage
                                        src={agent.profileImageUrl || agent.profile_image_url || undefined}
                                        alt={agent.name}
                                        className="object-cover"
                                      />
                                      <AvatarFallback className="text-sm font-bold bg-primary text-primary-foreground">
                                        {getInitialsFromFullName(agent.name.split(" ")[0] || "U", agent.name.split(" ")[1] || "N")}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <div className="font-semibold">
                                        {onDirect130 ? shortName(agent.name) : agent.name}
                                        {isCurrentUser && (
                                          <Badge
                                            className="ml-2 text-xs"
                                            variant="secondary"
                                          >
                                            You
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                              );
                            }

                            if (column.key === 'agency') {
                              return (
                                <TableCell key={column.key}>
                                  {getAgencyDisplay(agent)}
                                </TableCell>
                              );
                            }

                            // Goal and close-rate cells carry a second line of
                            // context; render them as a small stack instead of a
                            // string with parentheses that wraps mid-number.
                            if (column.key === 'goalProgress' || column.key === 'conversionRate') {
                              return (
                                <TableCell
                                  key={column.key}
                                  className={`text-center ${column.isPrimary ? 'bg-primary/5 border-x border-primary/30' : ''}`}
                                >
                                  {column.key === 'goalProgress' ? <GoalCell agent={agent} emphasised={!!column.isPrimary} /> : <CloseRateCell agent={agent} emphasised={!!column.isPrimary} />}
                                </TableCell>
                              );
                            }

                            // Handle data columns
                            const value = getCellValue(agent, column.key);
                            const isPrimary = column.isPrimary;

                            return (
                              <TableCell
                                key={column.key}
                                className={`text-center ${
                                  isPrimary ? 'bg-primary/5 border-x border-primary/30' : ''
                                }`}
                              >
                                <span className={`font-semibold ${
                                  isPrimary
                                    ? 'text-lg text-green-600 font-bold'
                                    : column.key === 'issuedPremium'
                                      // Sold premium is what agents are judged on,
                                      // so it stays emphasised even when another
                                      // metric is the active sort.
                                      ? 'font-bold text-emerald-700'
                                      : column.key === 'totalPremium'
                                        ? 'text-blue-600'
                                        : ''
                                }`}>
                                  {value}
                                </span>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
            </div>
          )}

          {/* Shell column-help panel (see helpTip state) */}
          {helpTip && (
            <>
              <div className="fixed inset-0 z-[9998]" onClick={() => setHelpTip(null)} />
              <div
                className="fixed z-[9999] w-[240px] rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-xl"
                style={{ top: helpTip.top, left: helpTip.left }}
              >
                {helpTip.text}
              </div>
            </>
          )}

          {/* Pagination Controls */}
          {agents.length > 0 && (
            <div className="leader-pager flex items-center justify-between border-t pt-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => setItemsPerPage(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={itemsPerPage.toString()} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, agents.length)} of {agents.length} agents
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first page, last page, current page, and pages around current
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, arr) => {
                        // Add ellipsis if there's a gap
                        const prevPage = arr[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && (
                              <span className="px-2 text-muted-foreground">...</span>
                            )}
                            <Button
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                              className="min-w-[36px]"
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        );
                      })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** "147%" over a thin track, then "$17,592 / $12,000 · this month" on one muted line. */
function GoalCell({ agent, emphasised }: { agent: LeaderboardAgent; emphasised: boolean }) {
  if (agent.goalProgress == null) {
    return <span className="text-sm text-gray-400">No goal</span>;
  }
  const pct = agent.goalProgress;
  const hit = pct >= 100;
  // Server label: "cur / target noun when · X default". Numbers on their own line,
  // the period as a tag by the %, the source only on hover — nothing wraps.
  const [fraction = '', source] = (agent.goalLabel || '').split(' · ');
  const m = /^(\S+) \/ (\S+) (.+?) (today|this week|this month)$/.exec(fraction);
  const numbers = m ? `${m[1]} / ${m[2]}` : fraction;
  const period = m ? { today: 'today', 'this week': 'week', 'this month': 'month' }[m[4]] : '';
  return (
    <div className="mx-auto flex w-36 flex-col items-stretch gap-1" title={agent.goalLabel || undefined}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={`tabular-nums ${emphasised ? 'text-lg font-bold' : 'text-sm font-semibold'} ${hit ? 'text-emerald-600' : 'text-gray-900'}`}>{pct.toFixed(0)}%</span>
        <span className="text-[10px] uppercase tracking-wide text-gray-400">{period}{source ? ' · default' : ''}</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
        <div className={`h-full rounded-full ${hit ? 'bg-emerald-500' : 'bg-primary'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      {numbers && <span className="whitespace-nowrap text-xs tabular-nums text-gray-500">{numbers}</span>}
    </div>
  );
}

/** "79.2%" with "8 closed · 2 lost" beneath, so a 100% on one lead reads as what it is. */
function CloseRateCell({ agent, emphasised }: { agent: LeaderboardAgent; emphasised: boolean }) {
  const f = decisionFraction(agent);
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`tabular-nums ${emphasised ? 'text-lg font-bold text-green-600' : 'text-sm font-semibold text-gray-900'}`}>{(agent.conversionRate || 0).toFixed(1)}%</span>
      {f && <span className="whitespace-nowrap text-xs text-gray-500">{f}</span>}
    </div>
  );
}
